import { supabase } from '../lib/supabase';

export const practiceSessionService = {
  /**
   * Get all practice workflows for the current user
   */
  async getWorkflows() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('practice_workflows')?.select(`
        *,
        practice_workflow_steps (
          id,
          tool_type,
          step_order,
          duration_minutes,
          exercise_id,
          settings,
          warm_up_exercises (
            id,
            title,
            exercise_type
          )
        )
      `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(workflow => ({
      id: workflow?.id,
      userId: workflow?.user_id,
      name: workflow?.name,
      description: workflow?.description,
      isPreset: workflow?.is_preset,
      presetName: workflow?.preset_name,
      totalDurationMinutes: workflow?.total_duration_minutes,
      createdAt: workflow?.created_at,
      updatedAt: workflow?.updated_at,
      steps: workflow?.practice_workflow_steps
        ?.sort((a, b) => a?.step_order - b?.step_order)?.map(step => ({
          id: step?.id,
          toolType: step?.tool_type,
          stepOrder: step?.step_order,
          durationMinutes: step?.duration_minutes,
          exerciseId: step?.exercise_id,
          settings: step?.settings,
          exercise: step?.warm_up_exercises ? {
            id: step?.warm_up_exercises?.id,
            title: step?.warm_up_exercises?.title,
            exerciseType: step?.warm_up_exercises?.exercise_type
          } : null
        })) || []
    }));
  },

  /**
   * Get practice sessions with analytics for current user
   */
  async getSessions(filters = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('practice_sessions')?.select(`
        *,
        practice_workflows (
          id,
          name,
          preset_name
        ),
        practice_session_segments (
          id,
          tool_type,
          segment_order,
          started_at,
          completed_at,
          duration_seconds,
          performance_score,
          metrics
        )
      `)?.eq('user_id', user?.id);

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate);
    }

    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate);
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(session => ({
      id: session?.id,
      userId: session?.user_id,
      workflowId: session?.workflow_id,
      sessionName: session?.session_name,
      status: session?.status,
      startedAt: session?.started_at,
      completedAt: session?.completed_at,
      totalDurationSeconds: session?.total_duration_seconds,
      completionRate: parseFloat(session?.completion_rate || 0),
      overallScore: parseFloat(session?.overall_score || 0),
      notes: session?.notes,
      createdAt: session?.created_at,
      updatedAt: session?.updated_at,
      workflow: session?.practice_workflows ? {
        id: session?.practice_workflows?.id,
        name: session?.practice_workflows?.name,
        presetName: session?.practice_workflows?.preset_name
      } : null,
      segments: session?.practice_session_segments
        ?.sort((a, b) => a?.segment_order - b?.segment_order)?.map(segment => ({
          id: segment?.id,
          toolType: segment?.tool_type,
          segmentOrder: segment?.segment_order,
          startedAt: segment?.started_at,
          completedAt: segment?.completed_at,
          durationSeconds: segment?.duration_seconds,
          performanceScore: parseFloat(segment?.performance_score || 0),
          metrics: segment?.metrics
        })) || []
    }));
  },

  /**
   * Get practice session analytics
   */
  async getAnalytics(filters = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('practice_session_analytics')?.select('*')?.eq('user_id', user?.id);

    if (filters?.startDate) {
      query = query?.gte('completed_at', filters?.startDate);
    }

    if (filters?.endDate) {
      query = query?.lte('completed_at', filters?.endDate);
    }

    query = query?.order('completed_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(row => ({
      sessionId: row?.session_id,
      userId: row?.user_id,
      sessionName: row?.session_name,
      status: row?.status,
      completedAt: row?.completed_at,
      totalDurationSeconds: row?.total_duration_seconds,
      completionRate: parseFloat(row?.completion_rate || 0),
      overallScore: parseFloat(row?.overall_score || 0),
      workflowName: row?.workflow_name,
      presetName: row?.preset_name,
      totalSegments: row?.total_segments,
      completedSegments: row?.completed_segments,
      avgSegmentScore: parseFloat(row?.avg_segment_score || 0),
      toolBreakdown: row?.tool_breakdown
    }));
  },

  /**
   * Create a new practice workflow
   */
  async createWorkflow(workflowData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: workflow, error: workflowError } = await supabase?.from('practice_workflows')?.insert({
        user_id: user?.id,
        name: workflowData?.name,
        description: workflowData?.description,
        total_duration_minutes: workflowData?.totalDurationMinutes,
        is_preset: false
      })?.select()?.single();

    if (workflowError) throw workflowError;

    if (workflowData?.steps && workflowData?.steps?.length > 0) {
      const steps = workflowData?.steps?.map((step, index) => ({
        workflow_id: workflow?.id,
        tool_type: step?.toolType,
        step_order: index + 1,
        duration_minutes: step?.durationMinutes,
        exercise_id: step?.exerciseId || null,
        settings: step?.settings || {}
      }));

      const { error: stepsError } = await supabase?.from('practice_workflow_steps')?.insert(steps);

      if (stepsError) throw stepsError;
    }

    return {
      id: workflow?.id,
      userId: workflow?.user_id,
      name: workflow?.name,
      description: workflow?.description,
      totalDurationMinutes: workflow?.total_duration_minutes,
      createdAt: workflow?.created_at
    };
  },

  /**
   * Start a new practice session
   */
  async startSession(workflowId, sessionName) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('practice_sessions')?.insert({
        user_id: user?.id,
        workflow_id: workflowId,
        session_name: sessionName,
        status: 'in_progress',
        started_at: new Date()?.toISOString()
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      workflowId: data?.workflow_id,
      sessionName: data?.session_name,
      status: data?.status,
      startedAt: data?.started_at
    };
  },

  /**
   * Complete a practice session
   */
  async completeSession(sessionId, sessionData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('practice_sessions')?.update({
        status: 'completed',
        completed_at: new Date()?.toISOString(),
        total_duration_seconds: sessionData?.totalDurationSeconds,
        completion_rate: sessionData?.completionRate,
        overall_score: sessionData?.overallScore,
        notes: sessionData?.notes || null
      })?.eq('id', sessionId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      status: data?.status,
      completedAt: data?.completed_at,
      completionRate: parseFloat(data?.completion_rate),
      overallScore: parseFloat(data?.overall_score)
    };
  },

  /**
   * Add segment to practice session
   */
  async addSessionSegment(sessionId, segmentData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('practice_session_segments')?.insert({
        session_id: sessionId,
        workflow_step_id: segmentData?.workflowStepId || null,
        tool_type: segmentData?.toolType,
        segment_order: segmentData?.segmentOrder,
        started_at: segmentData?.startedAt,
        completed_at: segmentData?.completedAt || null,
        duration_seconds: segmentData?.durationSeconds || null,
        performance_score: segmentData?.performanceScore || null,
        pitch_session_id: segmentData?.pitchSessionId || null,
        exercise_progress_id: segmentData?.exerciseProgressId || null,
        recommendation_id: segmentData?.recommendationId || null,
        metrics: segmentData?.metrics || {}
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      sessionId: data?.session_id,
      toolType: data?.tool_type,
      segmentOrder: data?.segment_order,
      performanceScore: parseFloat(data?.performance_score || 0)
    };
  },

  /**
   * Get user's practice streak and statistics
   */
  async getStreakStats() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('practice_sessions')?.select('completed_at, overall_score')?.eq('user_id', user?.id)?.eq('status', 'completed')?.order('completed_at', { ascending: false })?.limit(30);

    if (error) throw error;

    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;
    let lastDate = null;

    data?.forEach(session => {
      const sessionDate = new Date(session.completed_at)?.toDateString();
      
      if (!lastDate) {
        tempStreak = 1;
        currentStreak = 1;
      } else {
        const daysDiff = Math.floor((new Date(lastDate) - new Date(sessionDate)) / (1000 * 60 * 60 * 24));
        
        if (daysDiff === 1) {
          tempStreak++;
          currentStreak = tempStreak;
        } else if (daysDiff > 1) {
          longestStreak = Math.max(longestStreak, tempStreak);
          tempStreak = 1;
          currentStreak = 0;
        }
      }
      
      lastDate = sessionDate;
    });

    longestStreak = Math.max(longestStreak, tempStreak);

    const totalSessions = data?.length;
    const avgScore = data?.length > 0 
      ? data?.reduce((sum, s) => sum + parseFloat(s?.overall_score || 0), 0) / data?.length 
      : 0;

    return {
      currentStreak,
      longestStreak,
      totalSessions,
      avgScore: Math.round(avgScore * 100) / 100
    };
  }
};